import type { LabelPrintFormat } from "./types.js";

const VIRTUAL_PRINTER_RE =
  /onenote|pdf|xps|fax|microsoft print|send to|snagit|cutepdf|bullzip|adobe/i;

const THERMAL_PRINTER_RE =
  /4barcode|tsc|zebra|godex|gprinter|bixolon|thermal|label|barcode|intermec|datamax|sato|dymo/i;

export function isVirtualPrinterName(name: string): boolean {
  return VIRTUAL_PRINTER_RE.test(name);
}

export function isThermalPrinterName(name: string, driverName = ""): boolean {
  return THERMAL_PRINTER_RE.test(`${name} ${driverName}`);
}

export function sortPrintersForLabels<
  T extends { name: string; driverName: string; isDefault?: boolean },
>(printers: T[]): T[] {
  return [...printers].sort((a, b) => {
    const aThermal = isThermalPrinterName(a.name, a.driverName);
    const bThermal = isThermalPrinterName(b.name, b.driverName);
    if (aThermal !== bThermal) return aThermal ? -1 : 1;
    const aVirtual = isVirtualPrinterName(a.name);
    const bVirtual = isVirtualPrinterName(b.name);
    if (aVirtual !== bVirtual) return aVirtual ? 1 : -1;
    if (Boolean(a.isDefault) !== Boolean(b.isDefault)) return a.isDefault ? -1 : 1;
    return a.name.localeCompare(b.name, "fr");
  });
}

export function filterLabelPrinters<
  T extends { name: string; driverName: string; isDefault?: boolean },
>(printers: T[], showAll: boolean): T[] {
  const sorted = sortPrintersForLabels(printers);
  if (showAll) return sorted;
  const thermal = sorted.filter((p) => isThermalPrinterName(p.name, p.driverName));
  if (thermal.length > 0) return thermal;
  const physical = sorted.filter((p) => !isVirtualPrinterName(p.name));
  return physical.length > 0 ? physical : sorted;
}

export function findClosestLabelFormat(
  formats: LabelPrintFormat[],
  widthMm: number,
  heightMm: number,
  toleranceMm = 2,
): LabelPrintFormat | undefined {
  let best: LabelPrintFormat | undefined;
  let bestDelta = Number.POSITIVE_INFINITY;

  for (const format of formats) {
    const direct =
      Math.abs(format.widthMm - widthMm) + Math.abs(format.heightMm - heightMm);
    const swapped =
      Math.abs(format.widthMm - heightMm) + Math.abs(format.heightMm - widthMm);
    const delta = Math.min(direct, swapped);
    if (delta <= toleranceMm * 2 && delta < bestDelta) {
      best = format;
      bestDelta = delta;
    }
  }

  return best;
}

export function suggestFormatFromPaperSizes(
  formats: LabelPrintFormat[],
  paperSizes: Array<{ name: string; widthMm: number; heightMm: number }>,
): { format: LabelPrintFormat; paperName: string } | undefined {
  for (const paper of paperSizes) {
    const match = findClosestLabelFormat(formats, paper.widthMm, paper.heightMm);
    if (match) {
      return { format: match, paperName: paper.name };
    }
  }
  return undefined;
}
